import { useNavigate } from "react-router-dom";
import { PageHero, SectionTitle } from "@/components/Shared";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/Reveal";
import { Check, Info, CreditCard, ChevronRight, ShieldCheck } from "lucide-react";
import { PRICING, RESPONDER_PRICING, IMAGES, FAQS } from "@/data/site";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

export default function Pricing() {
  const navigate = useNavigate();
  return (
    <div data-testid="pricing-page">
      <PageHero
        eyebrow="PRICING & PACKAGES"
        title="Donate To Drive"
        subtitle="Transparent, walk-in suggested donations. No online payment needed — donate at the front desk. We are cashless: credit and debit cards only."
        image={IMAGES.motionBlur2}
        testid="pricing-hero"
      />

      <section className="bg-[#050505] py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <SectionTitle eyebrow="ARRIVE & DRIVE" title="Race Pricing" className="mb-4" />
          <p className="mb-6 max-w-2xl text-white/60">
            Every race is <span className="text-white">10 minutes</span> on track. Same suggested donation for adults and
            juniors — no booking required, donate on track by credit or debit card.
          </p>
          <p className="mb-14 max-w-2xl rounded-xl border border-kbyellow/30 bg-kbyellow/5 p-4 text-sm text-kbyellow/90">
            It's donate to drive — every donation on track directly funds the EduKarting Bahamas initiative,
            putting Bahamian youth behind the wheel.
          </p>
          <StaggerGroup className="grid gap-6 lg:grid-cols-3">
            {PRICING.map((p) => (
              <StaggerItem key={p.name}>
                <div
                  data-testid={`pricing-card-${p.name.toLowerCase().replace(/\s/g, "-")}`}
                  className={`group relative flex h-full flex-col overflow-hidden rounded-xl border p-8 transition-all hover:-translate-y-2 ${
                    p.highlight
                      ? "border-kbcyan bg-gradient-to-b from-kbcyan/10 to-transparent"
                      : "border-white/10 bg-obsidian hover:border-kbcyan/50"
                  }`}
                >
                  {p.tag && (
                    <span className={`absolute right-6 top-6 rounded-full px-3 py-1 text-xs font-semibold tracking-wide ${p.highlight ? "bg-kbyellow text-black" : "bg-white/10 text-white/70"}`}>
                      {p.tag}
                    </span>
                  )}
                  <h3 className="font-display text-4xl tracking-wide text-white">{p.name}</h3>
                  <p className="mt-2 text-sm text-white/50">{p.description}</p>
                  <div className="mt-6 flex items-end gap-1">
                    <span className="font-anton text-6xl text-white">{p.price}</span>
                    <span className="mb-2 text-white/50">{p.unit}</span>
                  </div>
                  <p className="mt-1 text-xs uppercase tracking-widest text-kbyellow/80">Suggested donation</p>
                  <div className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-kbyellow/10 px-3 py-1.5 text-xs text-kbyellow">
                    <ShieldCheck className="h-3.5 w-3.5" />
                    First responders: <span className="font-semibold">{p.responder}</span>
                  </div>
                  <ul className="mt-8 space-y-3">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-3 text-sm text-white/70">
                        <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-kbcyan" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-8 flex-1" />
                  <div className={`rounded-xl px-4 py-3 text-center font-display text-lg tracking-wide ${p.highlight ? "bg-kbcyan text-black" : "border border-white/15 text-white/70"}`}>
                    No booking required
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>

          <Reveal delay={0.1}>
            <div className="mt-10 flex flex-col items-start gap-4 rounded-xl border border-kbyellow/30 bg-kbyellow/5 p-6 md:flex-row md:items-center">
              <CreditCard className="h-8 w-8 flex-shrink-0 text-kbyellow" />
              <div>
                <h4 className="font-display text-2xl tracking-wide text-white">Donations Are Made On Track</h4>
                <p className="mt-1 text-sm text-white/60">
                  We are cashless — only credit and debit cards are accepted at the venue. There's no need to donate or reserve online for
                  arrive-and-drive racing — and every donation supports EduKarting Bahamas.
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div data-testid="first-responder-banner" className="mt-6 rounded-xl border border-kbcyan/30 bg-kbcyan/5 p-6">
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="flex items-start gap-4">
                  <ShieldCheck className="h-8 w-8 flex-shrink-0 text-kbcyan" />
                  <div>
                    <h4 className="font-display text-2xl tracking-wide text-white">20% First Responder Discount</h4>
                    <p className="mt-1 max-w-xl text-sm text-white/60">
                      Police, Firefighters, Defence Force, EMS and all first responders receive a 20% lower
                      suggested donation on every race package. Just show valid ID on track.
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3">
                  {RESPONDER_PRICING.map((r) => (
                    <div key={r.name} className="rounded-lg bg-black/40 px-4 py-2 text-center">
                      <div className="font-anton text-2xl text-kbcyan">{r.price}</div>
                      <div className="text-[11px] uppercase tracking-wide text-white/50">{r.name}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Group events callout */}
      <section className="bg-obsidian py-20 md:py-28">
        <div className="mx-auto flex max-w-[1400px] flex-col items-center gap-8 px-5 text-center md:px-10">
          <Info className="h-10 w-10 text-kbcyan" />
          <h2 className="max-w-2xl font-display text-4xl leading-[0.95] tracking-tight text-white md:text-6xl">
            Planning A Corporate Event Or Birthday?
          </h2>
          <p className="max-w-xl text-white/60">
            Corporate days, birthday parties and large group events are custom-built for you — pricing is
            provided by quote upon enquiry. Send us your details and we'll put together a tailored package.
          </p>
          <button
            onClick={() => navigate("/group-events")}
            data-testid="pricing-group-cta"
            className="group inline-flex items-center gap-2 rounded-xl bg-kbyellow px-7 py-4 font-display text-2xl tracking-wide text-black transition-transform hover:scale-[1.03]"
          >
            Request A Quote
            <ChevronRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </button>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#050505] py-24 md:py-32">
        <div className="mx-auto max-w-3xl px-5 md:px-10">
          <SectionTitle eyebrow="GOOD TO KNOW" title="Frequently Asked" className="mb-12" />
          <Accordion type="single" collapsible className="w-full" data-testid="pricing-faq">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-white/10">
                <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-display text-2xl tracking-wide text-white hover:text-kbcyan hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-base leading-relaxed text-white/60">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
}
